/**
 * accountGroupList.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import React from "react";
import { Link } from "react-router-dom";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";

import { Button, IconButton, TextField } from "@mui/material"

import { AuthContext } from "../auth/auth.context";
import { AccountGroupDetailComponent } from "./accountGroupDetail.component";
import { AccountNavContext } from "./account.context";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { Group, GroupState } from "../models/group";

import { groupServiceFactory } from "../services/group.service";
import { Tooltip } from "@mui/material";

export function AccountGroupListContainer(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const acctContext = React.useContext<AccountNavContext>(AccountNavContext);

  function searchGroups(accountID: string, search: string, limit: number): Promise<Array<Group>> {
    return groupServiceFactory().searchGroups(accountID, search, {limit: limit, all: true});
  }

  return <div className="account-group-list-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
          disableRipple disableTouchRipple
          onClick={acctContext.toggleLeftNav}>
        <MenuIcon />
      </IconButton>
      <h3>Groups: {authContext.membership?.accountname || ""}</h3>
    </header>
    <AccountGroupListComponent
      accountID={authContext.membership?.accountID || ""}
      searchGroups={searchGroups} />
  </div>;
}

interface Props {
  accountID: string,
  searchGroups(accountID: string, search: string, limit: number): Promise<Array<Group>>
}
export function AccountGroupListComponent(props: Props): React.ReactElement<any, any> {
  const [groups, setGroups] = React.useState<Array<Group>>([]);
  const [filterText, setFilterText] = React.useState<string>("");
  const [errMsg, setErrMsg] = React.useState("");
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [editSet, setEditSet] = React.useState<Set<number>>(new Set());

  React.useEffect(function () {
    if (!props.accountID) { return; }
    props.searchGroups(props.accountID, "", 300).then(setGroups, err => {
      console.error("Error fetching groups:", err);
      setErrMsg((err as Error).message);
    });
  }, [props.accountID]);

  function searchValueChange(event: React.SyntheticEvent) {
    const value = (event.target as HTMLInputElement).value;
    setFilterText(value);
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(setTimeout(async () => {
      try {
        if (!props.accountID) { return; }
        console.log("fetching members match:", value);
        const groups = await props.searchGroups(props.accountID, value, 300);
        setGroups(groups);
      } catch (err) {
        console.error("Error fetching members:", err);
        setErrMsg((err as Error).message);
      } finally {
        setTimer(null);
      }
    }, 1000));
  }

  function startEditAtIndex(index: number) {
    const newEditing = new Set([...editSet, index]);
    setEditSet(newEditing);
  }
  function finishEditAtIndex(index: number) {
    const newEditing = new Set(editSet);
    newEditing.delete(index);
    setEditSet(newEditing);
  }

  async function updateGroupName(accountID: string, groupID: string, groupname: string): Promise<Group | null> {
    try {
      const group = await groupServiceFactory().updateGroupName(accountID, groupID, {groupname});
      const newGroups = groups.map(g => g._id === groupID ? {...g, groupname} : g);
      setGroups(newGroups);
      return group;
    } catch (err) {
      console.error("Error saving group name:", err);
      setErrMsg((err as Error).message);
      return null;
    }
  }
  async function updateGroup(accountID: string, groupID: string, settings: Partial<Group>): Promise<Group | null> {
    try {
      const group = await groupServiceFactory().updateGroupSettings(accountID, groupID, settings);
      if (settings.groupstate) {
        const newGroups = groups.map(g => g._id === groupID ? {...g, ...settings} : g);
        setGroups(newGroups);
      }
      return group;
    } catch (err) {
      console.error("Error saving group update:", err);
      setErrMsg((err as Error).message);
      return null;
    }
  }

  return <>
    <TextField className="member-search" label="Filter" variant="outlined" fullWidth
        size="small" value={filterText} onChange={searchValueChange} />
    {groups.map((g, i) => editSet.has(i) ?
      <GroupEditItemComponent key={g._id} group={g} index={i} onFinishEdit={finishEditAtIndex}
        updateGroupName={updateGroupName}
        updateGroup={updateGroup}/> :
      <GroupListItemComponent key={g._id} group={g} index={i} doEditAt={startEditAtIndex} />)}
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") }/>
  </>;
}

interface GroupItemProps {
  group: Group,
  index: number,
  doEditAt(index: number): void
}
function GroupListItemComponent(props: GroupItemProps): React.ReactElement<any, any> {
  const userSet = new Set<string>();
  const devSet = new Set<string>();
  if (props.group.treenodes) {
    for (const node of props.group.treenodes) {
      if (node.userID && node.userID != props.group._id) { userSet.add(node.userID); }
      if (node.deviceID && node.deviceID != props.group._id) { devSet.add(node.deviceID); }
    }
  }
  let classes = "group-list-item";
  switch (props.group.groupstate || GroupState.active) {
    case GroupState.archived:
      classes += " archived";
      break;
    case GroupState.frozen:
      classes += " frozen";
      break;
    case GroupState.deleted:
      classes += " deleted";
      break;
  }
  return <article className={classes} onClick={() => props.doEditAt(props.index)}>
    <div>
      <div className="group-name">{props.group.groupname}</div>
      <div className="group-details">{userSet.size} users {"\u2022 "}
        {devSet.size} devices {props.group.desc && `\u2022 ${props.group.desc}`}</div>
    </div>
    <div className="right-controls">
      <IconButton size="small" component={Link} to={`/admin/group/${props.group._id}`}
            disableRipple disableTouchRipple>
        <ChevronRightIcon />
      </IconButton>
    </div>
  </article>;
}

interface EditProps {
  group: Group,
  index: number,
  updateGroupName(accountID: string, groupID: string, groupname: string): Promise<Group | null>,
  updateGroup(accountID: string, groupID: string, settings: Partial<Group>): Promise<Group | null>,
  onFinishEdit(index: number): void
}
function GroupEditItemComponent(props: EditProps): React.ReactElement<any, any> {

  function toggleGroupState() {
    const newState = props.group.groupstate === GroupState.active ? GroupState.archived : GroupState.active;
    props.updateGroup(props.group.accountID, props.group._id, {groupstate: newState});
  }

return <article className="group-list-edit-item form-wrapper">
    <AccountGroupDetailComponent group={props.group}
      updateGroupName={props.updateGroupName} updateGroup={props.updateGroup} />
    <div className="button-row">
      {!props.group.groupstate || props.group.groupstate === GroupState.active ?
        <Tooltip title="Hides this group, prevents future messages">
          <Button color="secondary" disableRipple disableTouchRipple
            onClick={toggleGroupState}>Archive</Button>
        </Tooltip> :
      props.group.groupstate === GroupState.archived ?
        <Tooltip title="Restores the group to active status">
          <Button color="secondary" disableRipple disableTouchRipple
            onClick={toggleGroupState}>Activate</Button>
        </Tooltip>: <></>
      }
      <Button variant="contained" color="primary"
        disableRipple disableTouchRipple
        onClick={() => props.onFinishEdit(props.index)}>Done</Button>
    </div>
  </article>;
}
